/** Header chrome for Inventory desk: title, Stock|Count|Missing toggle, refresh. */
import {
  ghostBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

export type InventoryDeskSurface = "stock" | "missing";

const segmentBase =
  "rounded-none border-0 px-3 py-1.5 text-sm font-semibold transition disabled:opacity-50";

const missingAlertClass =
  "bg-amber-100 text-amber-950 shadow-[0_0_0_1px_rgba(245,158,11,0.55),0_0_14px_rgba(245,158,11,0.45)] dark:bg-amber-950/60 dark:text-amber-100";

export function ManufacturingInventoryDeskHeader({
  busy,
  refreshing,
  onRefresh,
  onOpenInventoryCount,
  onOpenPurchasing,
  activeSurface,
  onSurfaceChange,
  missingCount = 0,
}: {
  busy: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  onOpenInventoryCount?: () => void;
  onOpenPurchasing?: () => void;
  activeSurface: InventoryDeskSurface;
  onSurfaceChange: (surface: InventoryDeskSurface) => void;
  missingCount?: number;
}) {
  const hasMissing = missingCount > 0;

  return (
    <div className="flex flex-wrap items-center gap-3 justify-between">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Inventory</h2>
        <div className="flex overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700">
          <button
            type="button"
            className={`${segmentBase} ${
              activeSurface === "stock" ? mfgSecondaryBtnClass : ghostBtnClass
            }`}
            aria-current={activeSurface === "stock" ? "page" : undefined}
            onClick={() => onSurfaceChange("stock")}
          >
            Stock
          </button>
          <button
            type="button"
            className={`${segmentBase} ${ghostBtnClass} border-l border-gray-200 dark:border-zinc-700`}
            onClick={() => onOpenInventoryCount?.()}
            disabled={!onOpenInventoryCount}
            title="Open inventory count session"
          >
            Count
          </button>
          <button
            type="button"
            className={
              `${segmentBase} border-l border-gray-200 dark:border-zinc-700 ` +
              (activeSurface === "missing"
                ? hasMissing
                  ? missingAlertClass
                  : mfgSecondaryBtnClass
                : hasMissing
                  ? missingAlertClass
                  : ghostBtnClass)
            }
            aria-current={activeSurface === "missing" ? "page" : undefined}
            onClick={() => onSurfaceChange("missing")}
            title={
              hasMissing
                ? `${missingCount} missing material match${missingCount === 1 ? "" : "es"} waiting`
                : "No missing material matches waiting"
            }
          >
            Missing{hasMissing ? ` (${missingCount})` : ""}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {onOpenPurchasing ? (
          <button
            type="button"
            className={ghostBtnClass}
            onClick={onOpenPurchasing}
            title="Open Production → Purchasing (POs and invoice receipts)"
          >
            Purchasing
          </button>
        ) : null}
        <button type="button" className={ghostBtnClass} onClick={onRefresh} disabled={busy}>
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>
    </div>
  );
}
